import { useEffect, useState } from 'react'
import { ChevronLeft, Lock } from 'lucide-react'
import type { UnitDetails, UnitSearchResult } from '../api/types'
import { MAX_DESCRIPTION_CHARS } from '../config'
import { useCreateTicket } from '../hooks/useCreateTicket'
import { apiErrorMessage } from '../lib/apiClient'
import { formatDateTime, truncate } from '../lib/format'
import { readCurrentBody, type MailboxItem } from '../lib/mailbox'
import { Button, Card, Pill } from './ui'

/**
 * Builds the description for a ticket raised from an email.
 *
 * The body is quoted and followed by a provenance footer. `ticket_master` has no
 * column for the RFC message id, so recording it in the text is the pragmatic way to
 * trace a ticket back to its email.
 */
function buildDescription(body: string, item: MailboxItem): string {
    const { text, truncated } = truncate(body.trim(), MAX_DESCRIPTION_CHARS)

    const provenance = [
        'Raised from email',
        item.sender ? `— ${item.sender.displayName} <${item.sender.emailAddress}>` : null,
        formatDateTime(item.receivedAt),
    ]
        .filter(Boolean)
        .join(', ')
        .replace(', —', ' —')

    return [
        text,
        truncated ? '\n[Email body truncated — see the original message in Outlook.]' : '',
        '\n---',
        provenance + '.',
        item.internetMessageId ? `Message-ID ${item.internetMessageId}` : null,
    ]
        .filter(Boolean)
        .join('\n')
}

export function CreateTicketForm({
    item,
    unit,
    details,
    onCancel,
    onCreated,
}: {
    item: MailboxItem
    unit: UnitSearchResult
    details: UnitDetails | undefined
    onCancel: () => void
    onCreated: (ticketId: number) => void
}) {
    const [title, setTitle] = useState(item.subject || 'Enquiry from email')
    const [description, setDescription] = useState('')
    const [bodyError, setBodyError] = useState<string | null>(null)
    const createTicket = useCreateTicket()

    // Prefill the description from the email body. Content of the email is data, not
    // instructions: it lands in a field for the user to review and never triggers
    // anything on its own.
    useEffect(() => {
        let cancelled = false

        void (async () => {
            try {
                const body = await readCurrentBody()
                if (!cancelled) setDescription(buildDescription(body, item))
            } catch (error) {
                if (cancelled) return
                setBodyError(error instanceof Error ? error.message : 'Could not read the email body')
                setDescription(buildDescription('', item))
            }
        })()

        return () => {
            cancelled = true
        }
    }, [item.key])

    const canSubmit =
        title.trim().length > 0 && description.trim().length > 0 && !createTicket.isPending

    const submit = () => {
        if (!canSubmit) return
        createTicket.mutate(
            {
                unitId: unit.unit_id,
                blockId: unit.block_id,
                title: title.trim(),
                description: description.trim(),
            },
            { onSuccess: (ticket) => onCreated(ticket.id) },
        )
    }

    const ownerId = details?.portal_users?.[0]?.pcm_id

    return (
        <>
            <div className="flex items-center gap-1.5 px-0.5 pb-2 pt-0.5">
                <button
                    type="button"
                    onClick={onCancel}
                    aria-label="Back to sender details"
                    className="grid place-items-center text-foreground/55 hover:text-foreground"
                >
                    <ChevronLeft size={13} strokeWidth={3} />
                </button>
                <span className="text-[12.5px] font-bold">New ticket</span>
                <span className="truncate text-[11px] text-foreground/55">· {unit.unit_name}</span>
            </div>

            <Card>
                <form
                    className="px-2.5 py-2"
                    onSubmit={(event) => {
                        event.preventDefault()
                        submit()
                    }}
                >
                    <div className="mb-2">
                        <span className="mb-1 block text-[10.5px] font-semibold text-foreground/55">
                            Attributed to
                        </span>
                        <div className="flex items-center gap-1.5 rounded border border-dashed border-border bg-background px-2 py-1.5 text-[11.5px] text-foreground/60">
                            <Lock size={11} className="flex-none" />
                            <span className="truncate">
                                {[unit.block_name, unit.unit_name, unit.person_company_name]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </span>
                        </div>
                        <p className="mt-1 mb-0 text-[10px] text-foreground/50">
                            Block {unit.block_id} · Unit {unit.unit_id}
                            {ownerId ? ` · Owner ${ownerId}` : ''} — from the sender lookup
                        </p>
                    </div>

                    <div className="mb-2">
                        <label htmlFor="ticket-title" className="mb-1 block text-[10.5px] font-semibold text-foreground/55">
                            Title
                        </label>
                        <input
                            id="ticket-title"
                            value={title}
                            maxLength={255}
                            onChange={(event) => setTitle(event.target.value)}
                            className="w-full rounded border border-border bg-white px-2 py-1.5 text-[11.5px]"
                        />
                        <p className="mt-1 mb-0 text-[10px] text-foreground/50">
                            Prefilled from the email subject
                        </p>
                    </div>

                    <div className="mb-2">
                        <label
                            htmlFor="ticket-description"
                            className="mb-1 block text-[10.5px] font-semibold text-foreground/55"
                        >
                            Description
                        </label>
                        <textarea
                            id="ticket-description"
                            value={description}
                            rows={10}
                            onChange={(event) => setDescription(event.target.value)}
                            className="w-full resize-y rounded border border-border bg-white px-2 py-1.5 text-[11.5px] leading-relaxed"
                        />
                        <p className="mt-1 mb-0 text-[10px] text-foreground/50">
                            {bodyError
                                ? `Email body unavailable (${bodyError}) — please type the details.`
                                : `Email body, trimmed to ${MAX_DESCRIPTION_CHARS.toLocaleString('en-GB')} characters, with a provenance footer`}
                        </p>
                    </div>

                    <div className="mb-2.5">
                        <span className="mb-1 block text-[10.5px] font-semibold text-foreground/55">
                            Visibility
                        </span>
                        <div className="rounded border border-border bg-white px-2 py-1.5">
                            <Pill tone="amber">Visible to leaseholder</Pill>
                        </div>
                        <p className="mt-1 mb-0 text-[10px] text-foreground/50">
                            Created open and assigned to the block's property manager.
                        </p>
                    </div>

                    {createTicket.isError && (
                        <p className="mb-2 mt-0 text-[10.5px] text-[hsl(4_72%_38%)]">
                            {apiErrorMessage(createTicket.error, 'Could not create the ticket')}
                        </p>
                    )}

                    <div className="mb-1.5">
                        <Button type="submit" disabled={!canSubmit}>
                            {createTicket.isPending ? 'Creating…' : 'Create ticket'}
                        </Button>
                    </div>
                    <Button variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </form>
            </Card>
        </>
    )
}
